<template>
  <div class="add-cert-form">
    <!-- Alert mesajı -->
    <div v-if="alertMessage" class="add-cert-alert">
      <AlertMessage :message="alertMessage" @remove="removeAlertMessage"/>
    </div>

    <!-- Dosya Yükleme -->
    <div class="add-cert-field">
      <label class="add-cert-label">{{ $t('general.file_pdf_image') }}</label>
      <div class="add-cert-upload" @click="triggerFileInput">
        <input ref="fileInput" accept="application/pdf,image/*" style="display:none;" type="file"
               @change="onFileChange">
        <div class="add-cert-upload__content">
          <span uk-icon="icon: cloud-upload; ratio:2"></span>
          <div class="add-cert-upload__text">
            <span v-if="!fileName">{{ $t('general.file_pdf_image_click') }}</span>
            <span v-else>
              <template v-if="isImage">
                <img :src="filePreview" alt="Önizleme" class="add-cert-upload__preview"/>
                <br>{{ fileName }}
              </template>
              <template v-else>
                <span uk-icon="icon: file-pdf"></span> {{ fileName }}
              </template>
            </span>
          </div>
        </div>
      </div>
    </div>

    <div class="add-cert-grid">
      <!-- Sertifika Adı -->
      <SfInput v-model="form.name"
               :label="$t('certificate.name')"
               :placeholder="$t('certificate.name')"
               name="cert-name"
      />
      <!-- Yetkilendiren Kişi -->
      <SfInput v-model="form.certifying_name"
               :label="$t('general.name_of_certificate_owner')"
               :placeholder="$t('general.name_of_certificate_owner')"
               name="cert-owner"
      />
      <!-- Yetkilendiren Ünvanı -->
      <SfInput v-model="form.certifying_title"
               :label="$t('general.name_of_certificate_institue')"
               :placeholder="$t('general.name_of_certificate_institue')"
               name="cert-institute"
      />
      <!-- Eğitim Adı -->
      <SfInput v-model="form.training_name"
               :label="$t('general.name_of_education_received')"
               :placeholder="$t('general.name_of_education_received')"
               name="cert-training"
      />

      <!-- Sertifika Alınma Tarihi -->
      <div class="add-cert-field">
        <label class="add-cert-label">{{ $t('general.certificate_received_date') }}</label>
        <vue-ctk-date-time-picker v-model="receivedDatePickerValue"
                                  :auto-close="true"
                                  :format="'YYYY-MM-DD'"
                                  :label="$t('general.choose_date')"
                                  :minute-interval="0"
                                  :no-button-clear="true"
                                  :no-button-now="true"
                                  :no-time="true"
                                  :only-date="true"
                                  :overlay="true"
                                  input-class="add-cert-input"
                                  position="top"
        />
      </div>

      <!-- Bitiş Tarihi -->
      <div class="add-cert-field">
        <label class="add-cert-label">{{ $t('general.certificate_end_date') }}</label>
        <vue-ctk-date-time-picker v-model="datePickerValue"
                                  :auto-close="true"
                                  :class="{ 'add-cert-disabled': form.is_unlimited }"
                                  :disabled="form.is_unlimited"
                                  :format="'YYYY-MM-DD'"
                                  :label="$t('general.choose_date')"
                                  :minute-interval="0"
                                  :no-button-clear="true"
                                  :no-button-now="true"
                                  :no-time="true"
                                  :only-date="true"
                                  :overlay="true"
                                  input-class="add-cert-input"
                                  position="top"
        />
        <label class="add-cert-checkbox">
          <input v-model="form.is_unlimited"
                 class="add-cert-checkbox__input"
                 type="checkbox"
                 @change="onUnlimitedChange"
          />
          <span class="add-cert-checkbox__text">{{ $t('general.certificate_shall_be_indefinite') }}</span>
        </label>
      </div>

      <!-- Mail Bildirimi -->
      <div class="add-cert-field">
        <label class="add-cert-label">
          {{ $t('general.mail_notification') }}
          <span class="add-cert-badge">{{ $t('general.after_date_done') }}</span>
        </label>
        <app-multiselect v-model="mailNotifyOption"
                         :allow-empty="false"
                         :close-on-select="true"
                         :hide-label="true"
                         :is-multiple="false"
                         :is-searchable="false"
                         :options="yesNoOptions"
                         label="name"
                         placeholder="Seçiniz"
                         track-by="id"
        />
      </div>

      <!-- Sertifika Türü -->
      <div class="add-cert-field">
        <label class="add-cert-label">{{ $t("general.certificate_type") }}</label>
        <MultipleSelectInput v-model="selectedCertificateTypes"
                             :allow-empty="true"
                             :multiple="true"
                             :searchable="true"
                             :show-labels="true"
                             :taggable="true"
                             :values="certificateTypeOptions"
                             label="name"
                             track-by="id"
                             @addTag="handleCertificateTypeTag"
        />
      </div>

    </div>
  </div>
</template>

<script>
import VueCtkDateTimePicker from 'vue-ctk-date-time-picker';
import AppMultiselect from '@/view/components/select/app-multiselect/app-multiselect.vue';
import AlertMessage from '@/view/components/notification/AlertMessage.vue';
import SfInput from '@/view/components/ui/SfInput.vue';
import bridge from '@/core/services/bridge.service';
import native from '@/mobile/mobile.fileHandler';
import MultipleSelectInput from "@/assets/components/inputs/MultipleSelectInput";
import certificateTypeMixin from "@/view/company/Certificates/certificateTypeMixin";

export default {
  name: "AddCertificateModal",
  components: { VueCtkDateTimePicker, AppMultiselect, AlertMessage, MultipleSelectInput, SfInput },
  mixins: [certificateTypeMixin],
  props: {
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      form: {
        file: null,
        name: "",
        certifying_name: "",
        certifying_title: "",
        received_date: "",
        end_date: "",
        mail_notify: "1",
        training_name: "",
        is_unlimited: false
      },
      fileName: "",
      filePreview: null,
      isImage: false,
      alertMessage: null,
      yesNoOptions: [
        { id: '1', name: this.$t('general.yes') },
        { id: '0', name: this.$t('general.no') }
      ]
    };
  },
  computed: {
    receivedDatePickerValue: {
      get() {
        if (!this.form.received_date) return '';
        return this.form.received_date.split('T')[0];
      },
      set(val) {
        this.form.received_date = val ? val.split('T')[0] : '';
      }
    },
    mailNotifyOption: {
      get() {
        return this.yesNoOptions.find(opt => opt.id === this.form.mail_notify);
      },
      set(val) {
        this.form.mail_notify = val ? val.id : '';
      }
    },
    datePickerValue: {
      get() {
        if (!this.form.end_date) return '';
        return this.form.end_date.split('T')[0];
      },
      set(val) {
        this.form.end_date = val ? val.split('T')[0] : '';
      }
    }
  },
  mounted() {
    this.getCertificateTypes();

    if (bridge.isNative) {
      this._unsubscribePickFile = bridge.on('PICK_FILE_RESULT', (payload) => {
        if (!payload.success || !payload.file) return;
        const { name, type, base64 } = payload.file;
        const byteString = atob(base64);
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) {
          ia[i] = byteString.charCodeAt(i);
        }
        const file = new File([ab], name, { type });
        this.form.file = file;
        this.fileName = name;
        if (type.startsWith('image/')) {
          this.isImage = true;
          this.filePreview = 'data:' + type + ';base64,' + base64;
        } else {
          this.isImage = false;
          this.filePreview = null;
        }
      });
    }
  },

  beforeDestroy() {
    if (this._unsubscribePickFile) {
      this._unsubscribePickFile();
    }
  },

  methods: {
    triggerFileInput() {
      if (bridge.isNative) {
        native.pickFile('application/pdf,image/*');
        return;
      }
      this.$refs.fileInput.click();
    },
    onFileChange(e) {
      const file = e.target.files[0];
      this.form.file = file;
      this.fileName = file ? file.name : "";
      if (file && file.type.startsWith('image/')) {
        this.isImage = true;
        const reader = new FileReader();
        reader.onload = e => {
          this.filePreview = e.target.result;
        };
        reader.readAsDataURL(file);
      } else {
        this.isImage = false;
        this.filePreview = null;
      }
    },
    checkValidation() {
      this.alertMessage = null;

      if (!this.form.file) {
        this.alertMessage = { type: 'error', text: 'Dosya seçilmesi zorunludur!' };
        return false;
      }
      if (!this.form.name || this.form.name.trim() === '') {
        this.alertMessage = { type: 'error', text: 'Sertifika adı zorunludur!' };
        return false;
      }
      if (!this.form.certifying_name || this.form.certifying_name.trim() === '') {
        this.alertMessage = { type: 'error', text: 'Sertifika sahibinin adı zorunludur!' };
        return false;
      }
      if (!this.form.certifying_title || this.form.certifying_title.trim() === '') {
        this.alertMessage = { type: 'error', text: 'Sertifikalayan kurumun adı zorunludur!' };
        return false;
      }
      if (!this.form.training_name || this.form.training_name.trim() === '') {
        this.alertMessage = { type: 'error', text: 'Aldığı eğitim adı zorunludur!' };
        return false;
      }
      if (!this.form.received_date) {
        this.alertMessage = { type: 'error', text: 'Sertifika alınma tarihi zorunludur!' };
        return false;
      }
      if (!this.form.is_unlimited && !this.form.end_date) {
        this.alertMessage = { type: 'error', text: 'Sertifika bitiş tarihi zorunludur!' };
        return false;
      }
      return true;
    },

    removeAlertMessage() {
      this.alertMessage = null;
    },

    onSubmit() {
      if (!this.checkValidation()) return;

      const formData = new FormData();
      Object.keys(this.form).forEach(key => {
        formData.append(key, this.form[key]);
      });
      if (this.selectedCertificateTypes && this.selectedCertificateTypes.length) {
        this.selectedCertificateTypes.forEach((item) => {
          formData.append("type_ids[]", item.id);
        });
      }
      this.$emit('submit', formData);
    },

    onUnlimitedChange() {
      if (this.form.is_unlimited) {
        this.form.end_date = "";
      }
    }
  }
};
</script>

<style scoped>
/* ═══════════════════════════════════════
   GENEL (Web + Mobil) — MOBILE_UI_GUIDELINES
   ═══════════════════════════════════════ */

.add-cert-form {
  padding: 0;
}

.add-cert-alert {
  margin-bottom: 16px;
}

/* ── Grid: 2 sütun (web), 1 sütun (mobil) ── */
.add-cert-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

/* ── Field ── */
.add-cert-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

/* ── Full width field (spans both columns) ── */
.add-cert-field--full {
  grid-column: 1 / -1;
}

/* ── Label: 14px / 700 — SfInput ile aynı ── */
.add-cert-label {
  font-size: 14px;
  font-weight: 700;
  color: #333;
  line-height: 1.4;
  margin-bottom: 0;
}

/* SfInput grid içinde margin sıfırla */
.add-cert-grid .sf-input-wrapper {
  margin-bottom: 0;
}

/* ── Upload box: 14px radius, active state ── */
.add-cert-upload {
  border: 2px dashed #d3d3d3;
  border-radius: 14px;
  padding: 24px 16px;
  text-align: center;
  cursor: pointer;
  background: #fafbfc;
  transition: border-color 0.2s;
  margin-bottom: 8px;
  -webkit-tap-highlight-color: transparent;
}

.add-cert-upload:active {
  border-color: #2c3e50;
  background: #f5f6fa;
}

.add-cert-upload__content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.add-cert-upload__text {
  font-size: 16px;
  color: #666;
  line-height: 1.5;
}

.add-cert-upload__preview {
  max-width: 120px;
  max-height: 120px;
  border-radius: 8px;
  margin-bottom: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

/* ── Date picker ── */
.add-cert-disabled {
  opacity: 0.5;
  pointer-events: none;
}

/* ── Checkbox: min 44px touch target, flex row hizalı ── */
.add-cert-checkbox {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 12px;
  margin-top: 4px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #eef0f3;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.add-cert-checkbox:active {
  background: #f0f1f3;
}

.add-cert-checkbox__input {
  width: 20px;
  height: 20px;
  min-width: 20px;
  min-height: 20px;
  flex-shrink: 0;
  margin: 0;
  padding: 0;
  cursor: pointer;
  accent-color: #2c3e50;
  vertical-align: middle;
}

.add-cert-checkbox__text {
  font-size: 14px;
  font-weight: 500;
  color: #555;
  user-select: none;
  line-height: 44px;
}

/* ── Badge ── */
.add-cert-badge {
  font-size: 10px;
  color: #fff;
  background: rgb(85, 88, 232);
  padding: 2px 8px;
  border-radius: 12px;
  margin-left: 6px;
  display: inline-block;
  vertical-align: middle;
  border: 1px solid rgb(30, 34, 231);
}

/* ═══════════════════════════════════════
   MOBİL (≤768px)
   ═══════════════════════════════════════ */
@media (max-width: 768px) {
  .add-cert-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .add-cert-upload {
    padding: 24px 16px;
  }
}
</style>

<style>
/* ── Date picker dropdown (global) ── */
.add-cert-form .ctk-date-time-picker .datetimepicker {
  z-index: 99999 !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08) !important;
  border: 1px solid #e5e5e5 !important;
  border-radius: 8px !important;
}

/* Date picker input override */
.add-cert-form .ctk-date-time-picker .field-input {
  height: 48px !important;
  font-size: 16px !important;
  border: 1.5px solid #e0e0e0 !important;
  border-radius: 10px !important;
  padding-left: 16px !important;
  outline: none !important;
  box-shadow: none !important;
}

.add-cert-form .ctk-date-time-picker .field-input:focus,
.add-cert-form .ctk-date-time-picker .field-input:active,
.add-cert-form .ctk-date-time-picker .field-input:hover {
  border: 1.5px solid #e0e0e0 !important;
  outline: none !important;
  box-shadow: none !important;
}

/* Multiselect override */
.add-cert-form .multiselect__tags {
  border: 1.5px solid #e0e0e0 !important;
  border-radius: 10px !important;
  min-height: 48px !important;
  font-size: 16px !important;
  outline: none !important;
  box-shadow: none !important;
}

.add-cert-form .multiselect--active .multiselect__tags {
  border: 1.5px solid #e0e0e0 !important;
  box-shadow: none !important;
}

.add-cert-form .multiselect__content-wrapper {
  z-index: 99999 !important;
  border-radius: 8px !important;
}

.add-cert-form .multiselect__single,
.add-cert-form .multiselect__placeholder {
  font-size: 16px !important;
  padding-top: 8px !important;
}

/* MultipleSelectInput - reduced height and opens upward */
.add-cert-form .add-cert-field .custom-multiselect {
  height: auto !important;
  min-height: 44px !important;
  position: relative !important;
  margin-top: 4px !important;
}


.add-cert-form .custom-multiselect.multiselect--active .multiselect__tags {
  border: 1.5px solid #e0e0e0 !important;
  box-shadow: none !important;
  min-height: 48px !important;
  padding: 12px 40px 12px 12px !important;
}

.add-cert-form .custom-multiselect .multiselect__single,
.add-cert-form .custom-multiselect .multiselect__placeholder {
  font-size: 16px !important;
  padding: 0 !important;
  margin: 0 !important;
  line-height: 28px !important;
}

.add-cert-form .custom-multiselect .multiselect__input {
  min-height: 28px !important;
  line-height: 28px !important;
  padding: 0 !important;
  margin: 0 !important;
}

/* Dropdown opens upward */
.add-cert-form .custom-multiselect .multiselect__content-wrapper {
  bottom: 100% !important;
  top: auto !important;
  margin-bottom: 4px !important;
  z-index: 99999 !important;
  border-radius: 8px !important;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1) !important;
}
</style>
